import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import axios from 'axios';
import API_ENDPOINTS from '../../config/apiEndpoints';
import { FiPlus, FiEdit, FiSearch, FiX, FiCheck } from 'react-icons/fi';
import Loader from '../Loader/Loader';

const StatusAlert = ({ isOpen, onClose, type = 'success', title, message }) => {
  if (!isOpen) return null;
  const isSuccess = type === 'success';

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm p-8 flex flex-col items-center text-center animate-in fade-in zoom-in duration-200">
          <div className={`w-24 h-24 rounded-full border-4 flex items-center justify-center mb-6 ${
            isSuccess ? 'border-green-100 text-green-500' : 'border-red-100 text-red-500'
          }`}>
            <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
              isSuccess ? 'border-2 border-green-500' : 'border-2 border-red-500'
            }`}>
              {isSuccess ? <FiCheck size={48} /> : <FiX size={48} />}
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">{title || (isSuccess ? 'Success' : 'Error')}</h2>
          <p className="text-gray-500 text-lg mb-8 leading-relaxed">{message}</p>
          <button
            onClick={onClose}
            className="w-24 py-2.5 bg-[#6366f1] text-white rounded-md font-semibold text-lg hover:bg-[#4f46e5] transition-colors focus:outline-none shadow-md"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const MailReportModal = ({ isOpen, onClose, onAddNew, onEdit }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const TOKEN = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN;
  const NEW_BASE_URL = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
  const ustrUserId = localStorage.getItem('ustrUserId') || localStorage.getItem('userId') || localStorage.getItem('UserId') || '34594';
  const VIEW_API = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.VIEW_DASHBOARD_SETTING_BY_PARENTID}`;
  const Status_Change = `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.DASHBOARD_MAIL_SETTING_STATUS}`;
  const EDIT_MODE =  `${NEW_BASE_URL}${API_ENDPOINTS.DASHBOARD.VEIW_DASHBOARD_SETTINGBYID}`;

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          UserId: String(ustrUserId),
          strDashboardType: "MainDashboard"
        }
      };
      const response = await axios.post(VIEW_API, payload);
      let list = response.data?.Details || [];
      if (typeof list === 'string') {
        try { list = JSON.parse(list); } catch (e) { }
      }
      setData(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error fetching mail settings report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id) => {
    setLoading(true);
    try {
      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          strMailSettingID: String(id)
        }
      };
      const response = await axios.post(EDIT_MODE, payload);
      let details = response.data?.Details;
      if (typeof details === 'string') {
        try { details = JSON.parse(details); } catch (e) { }
      }
      
      if (Array.isArray(details) && details.length > 0) {
        onEdit(details[0]);
      } else {
        setErrorMsg('Could not fetch setting details.');
      }
    } catch (error) {
      console.error('Error fetching edit data:', error);
      setErrorMsg('Network error while fetching details.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    try {
      const date = new Date(dateString);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear()}`;
    } catch (e) {
      return dateString;
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      const payload = {
        Status: "",
        Message: "",
        Token: String(TOKEN),
        Details: {
          strMailSettingID: String(id),
          blIsActive: !currentStatus
        }
      };
      
      const res = await axios.post(Status_Change, payload);
      if (res.data && (res.data.Status === 1 || res.data.Status === "1")) {
        setShowSuccess(true);
        fetchData();
      } else {
        setErrorMsg(res.data?.Message || 'Something went wrong while updating status.');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      setErrorMsg('Network error. Please try again.');
    }
  };

  const filteredData = data.filter(item => 
    Object.values(item).some(val => 
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Report Email Setting"
      size="full"
    >
      <div className="flex flex-col h-[70vh]">
        {/* Header Actions */}
        <div className="flex justify-between items-center mb-6">
          <div className="relative w-72">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search settings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all shadow-sm"
            />
          </div>
          <button
            onClick={onAddNew}
            className="flex items-center gap-2 px-4 py-2 bg-[#00b0f0] text-white rounded-lg hover:bg-[#009bd4] transition-all font-semibold shadow-md active:scale-95"
          >
            <FiPlus size={20} />
            <span>Add New</span>
          </button>
        </div>

        {/* Table Container */}
        <div className="flex-1 overflow-auto border border-gray-100 rounded-xl bg-white shadow-sm">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader message="Fetching data..." inline />
            </div>
          ) : filteredData.length > 0 ? (
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Section</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">User Collection</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Mail Id's</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">Duration</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">Time</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Created Date</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">Scope</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">Status</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredData.map((item, idx) => (
                  <tr key={idx} className="hover:bg-blue-50/30 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-gray-700 capitalize">{item.Sections || '---'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{item.UserCollectionsByName || '---'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-[200px] truncate" title={item.EmailIDs}>{item.EmailIDs || '---'}</td>
                    <td className="px-6 py-4 text-sm text-center">
                      <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-semibold">{item.SendAtPeriod || 'Daily'}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 font-mono text-center">{item.SendAtTime || '---'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(item.CreatedOn)}</td>
                    <td className="px-6 py-4 text-sm text-center">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        (item.Scope || '').toLowerCase() === 'global' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>
                        {item.Scope || 'Global'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center">
                        <div 
                          onClick={() => handleStatusToggle(item.ID, item.IsActive)}
                          className={`w-12 h-6 rounded-full relative cursor-pointer transition-all ${
                            item.IsActive ? 'bg-blue-500' : 'bg-gray-300'
                          }`}
                        >
                          <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                            item.IsActive ? 'left-7' : 'left-1'
                          }`} />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button 
                        onClick={() => handleEdit(item.ID)}
                        className="text-gray-400 hover:text-blue-500 transition-colors"
                      >
                        <FiEdit size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 py-12">
              <FiX size={48} className="mb-4 opacity-20" />
              <p className="text-lg">No report data found</p>
            </div>
          )}
        </div>
      </div>

      {/* Success Alert */}
      <StatusAlert 
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        type="success"
        title="Success"
        message="Data Updated"
      />

      {/* Error Alert */}
      <StatusAlert 
        isOpen={!!errorMsg}
        onClose={() => setErrorMsg('')}
        type="error"
        title="Error"
        message={errorMsg}
      />
    </Modal>
  );
};

export default MailReportModal;
